import React, { useState, useEffect, useContext, useMemo } from "react";
import DOMPurify from "dompurify";
import { getNews } from "../services/newsServices";
import { LanguageContext } from "../contexts/LanguageContext";
import { useTranslation } from "react-i18next";

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3317";

const isExternalURL = (url) => {
  try {
    const newUrl = new URL(url);
    return newUrl.protocol === "http:" || newUrl.protocol === "https:";
  } catch (error) {
    return false;
  }
};

const absUrl = (u) => (u ? (isExternalURL(u) ? u : `${BASE_URL}${u}`) : "");

// Mini helper : on retire le HTML pour faire un extrait propre
const toTextExcerpt = (html = "", max = 140) => {
  const clean = DOMPurify.sanitize(String(html || ""), { ALLOWED_TAGS: [] });
  const s = clean.replace(/\s+/g, " ").trim();
  if (!s) return "";
  return s.length > max ? s.slice(0, max).trim() + "…" : s;
};

// Date affichage simple (si tu as createdAt / date)
const formatDate = (d, language) => {
  if (!d) return "";
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return "";
  return new Intl.DateTimeFormat(language === "FR" ? "fr-FR" : "en-GB", {
    dateStyle: "medium",
  }).format(dt);
};

const LatestNews = ({ sectionWrap, buttonPrimary, softText, mutedText }) => {
  const [newsList, setNewsList] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState({});
  const [currentPage, setCurrentPage] = useState(1);

  const newsPerPage = 3;

  const { language } = useContext(LanguageContext);
  const { t } = useTranslation();

  const getLocalizedKey = (key) => `${key}_${String(language || "FR").toLowerCase()}`;

  useEffect(() => {
    const fetchNews = async () => {
      try {
        const latestNews = await getNews();
        const activeNews = (latestNews || []).filter((n) => n.is_active);

        // Option : tri du plus récent au plus ancien si date dispo
        activeNews.sort((a, b) => {
          const da = new Date(a.createdAt || a.date || a.updatedAt || 0).getTime();
          const db = new Date(b.createdAt || b.date || b.updatedAt || 0).getTime();
          return db - da;
        });

        setNewsList(activeNews);

        // Index init pour carrousel interne
        const initialIndexes = activeNews.reduce(
          (acc, item) => ({ ...acc, [item.id]: 0 }),
          {}
        );
        setCurrentImageIndex(initialIndexes);
      } catch (error) {
        console.error("Failed to fetch news", error);
        setNewsList([]);
      }
    };

    fetchNews();
  }, []);

  // Slide auto uniquement si l'article a plusieurs images
  useEffect(() => {
    if (!newsList.length) return;

    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => {
        const next = { ...prev };

        for (const item of newsList) {
          const imgs = [item.image_url, item.image_url2].filter(Boolean);
          if (imgs.length <= 1) continue; // pas de rotation si 0/1 image

          const cur = prev[item.id] ?? 0;
          next[item.id] = (cur + 1) % imgs.length;
        }

        return next;
      });
    }, 5000);

    return () => clearInterval(interval);
  }, [newsList]);

  // Pagination
  const totalPages = Math.max(1, Math.ceil(newsList.length / newsPerPage));
  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [totalPages, currentPage]);

  const currentNews = useMemo(() => {
    const start = (currentPage - 1) * newsPerPage;
    return newsList.slice(start, start + newsPerPage);
  }, [newsList, currentPage]);

  const openModal = (imageUrl) => setSelectedImage(imageUrl);
  const closeModal = () => setSelectedImage(null);

  // Styles fallback si tu n’injectes pas ceux de HomePage
  const wrap = sectionWrap || "container mx-auto px-4";
  const titleTone = softText || "text-gray-900 dark:text-white";
  const textTone = mutedText || "text-gray-700 dark:text-gray-300";
  const cta = buttonPrimary || "inline-flex items-center justify-center rounded-2xl bg-primary px-6 py-3 text-white shadow hover:opacity-95 transition";

  return (
    <section className="py-10 sm:py-12">
      <div className={wrap}>
        {/* Header modern */}
        <div className="text-center mb-10">
          <div
            className="
              inline-flex items-center gap-2 rounded-full px-4 py-2
              bg-black/5 dark:bg-white/10
              border border-black/10 dark:border-white/10
              backdrop-blur-sm
            "
          >
            <span className="h-2 w-2 rounded-full bg-primary" />
            <span className="text-xs sm:text-sm font-satoshi font-semibold tracking-wide text-gray-700 dark:text-gray-200">
              {t("homepage.lastnews")}
            </span>
          </div>

          <h2
            className={`
              mt-4 text-3xl sm:text-4xl lg:text-5xl
              font-satoshi font-extrabold tracking-tight
              ${titleTone}
            `}
          >
            {language === "FR" ? "Dernières actualités" : "Latest news"}
          </h2>

          <div className="mt-4 flex justify-center">
            <div className="h-[3px] w-24 sm:w-32 rounded-full bg-primary/90" />
          </div>

          <p className={`mt-4 max-w-3xl mx-auto text-sm sm:text-base ${textTone}`}>
            {language === "FR"
              ? "Découvrez les dernières informations, évènements et nouveautés."
              : "Discover the latest updates, events and news."}
          </p>
        </div>

        {/* Grid cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 font-satoshi">
          {currentNews.map((item) => {
            const images = [item.image_url, item.image_url2].filter(Boolean);
            const hasImages = images.length > 0;

            const idx = currentImageIndex[item.id] ?? 0;
            const activeImage = hasImages ? images[idx % images.length] : null;

            const title =
              item[getLocalizedKey("title")] ||
              (language === "FR" ? "Titre indisponible" : "Title unavailable");

            const content =
              item[getLocalizedKey("content")] ||
              (language === "FR" ? "Contenu indisponible" : "Content unavailable");

            const excerpt = toTextExcerpt(content, 150);

            const displayDate = formatDate(
              item.createdAt || item.date || item.updatedAt,
              language
            );

            return (
              <article
                key={item.id}
                className="
                  group relative overflow-hidden
                  rounded-3xl
                  border border-black/10 dark:border-white/10
                  bg-[#E8BB89]/80 dark:bg-white/5
                  backdrop-blur-sm
                  shadow-sm
                  transition-all duration-300
                  hover:-translate-y-1 hover:shadow-lg
                "
              >
                {/* Image */}
                <button
                  type="button"
                  onClick={() => activeImage && openModal(absUrl(activeImage))}
                  className="relative w-full focus:outline-none"
                  aria-label={language === "FR" ? "Agrandir l'image" : "Open image"}
                  disabled={!activeImage}
                >
                  <div className="relative h-56 w-full overflow-hidden">
                    {activeImage ? (
                      <img
                        src={absUrl(activeImage)}
                        alt={title}
                        className="
                          h-full w-full object-cover
                          transition-transform duration-500 ease-out
                          group-hover:scale-[1.03]
                        "
                        loading="lazy"
                      />
                    ) : (
                      <div className="h-full w-full bg-black/5 dark:bg-white/10 flex items-center justify-center">
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          {language === "FR" ? "Aucune image" : "No image"}
                        </span>
                      </div>
                    )}

                    {/* overlay */}
                    <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/35 via-black/10 to-transparent opacity-90" />

                    {/* small badge date */}
                    {displayDate ? (
                      <div className="absolute top-3 left-3">
                        <div
                          className="
                            rounded-full px-3 py-1 text-xs
                            bg-white/70 dark:bg-black/50
                            backdrop-blur-md
                            border border-black/10 dark:border-white/10
                            text-gray-900 dark:text-white
                          "
                        >
                          {displayDate}
                        </div>
                      </div>
                    ) : null}

                    {/* open hint */}
                    {activeImage ? (
                      <div className="absolute bottom-3 right-3">
                        <div
                          className="
                            rounded-2xl px-3 py-2 text-xs sm:text-sm
                            bg-white/15 text-white
                            backdrop-blur-md
                            border border-white/15
                            opacity-0 group-hover:opacity-100
                            transition-opacity
                          "
                        >
                          {language === "FR" ? "Agrandir ⤢" : "Open ⤢"}
                        </div>
                      </div>
                    ) : null}
                  </div>
                </button>

                {/* Content */}
                <div className="p-5 sm:p-6">
                  <h3 className={`text-lg sm:text-xl font-satoshi font-bold ${titleTone}`}>
                    {title}
                  </h3>

                  <div className="mt-3 h-[2px] w-14 rounded-full bg-primary/80" />

                  <p className={`mt-4 text-sm leading-relaxed ${textTone}`}>
                    {excerpt}
                  </p>

                  <div className="mt-5">
                    <a
                      href="/news"
                      className="
                        inline-flex items-center gap-2
                        text-primary font-semibold
                        hover:underline
                      "
                    >
                      {language === "FR" ? "Lire la suite" : "Read more"}
                      <span aria-hidden="true">→</span>
                    </a>
                  </div>
                </div>
              </article>
            );
          })}
        </div>

        {/* Pagination modern */}
        {newsList.length > newsPerPage && (
          <div className="mt-8 flex items-center justify-center gap-3">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              className="
                rounded-2xl px-4 py-2
                bg-black/5 dark:bg-white/10
                border border-black/10 dark:border-white/10
                text-gray-900 dark:text-white
                hover:border-primary/60 transition
                disabled:opacity-50 disabled:cursor-not-allowed
              "
              disabled={currentPage === 1}
            >
              {language === "FR" ? "Précédent" : "Previous"}
            </button>

            <div className="text-sm text-gray-700 dark:text-gray-300">
              {language === "FR" ? "Page" : "Page"}{" "}
              <span className="font-semibold">{currentPage}</span> /{" "}
              <span className="font-semibold">{totalPages}</span>
            </div>

            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              className="
                rounded-2xl px-4 py-2
                bg-black/5 dark:bg-white/10
                border border-black/10 dark:border-white/10
                text-gray-900 dark:text-white
                hover:border-primary/60 transition
                disabled:opacity-50 disabled:cursor-not-allowed
              "
              disabled={currentPage === totalPages}
            >
              {language === "FR" ? "Suivant" : "Next"}
            </button>
          </div>
        )}

        {/* CTA */}
        <div className="text-center mt-10">
          <a href="/news" className={cta}>
            {t("homepage.button")}
          </a>
        </div>

        {/* Modal */}
        {selectedImage && (
          <div
            className="fixed inset-0 z-[999] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={closeModal}
            role="dialog"
            aria-modal="true"
          >
            <div className="relative max-w-[92vw] max-h-[85vh]">
              <img
                src={selectedImage}
                alt={language === "FR" ? "Agrandissement" : "Preview"}
                className="max-w-[92vw] max-h-[85vh] rounded-3xl shadow-2xl object-contain"
                onClick={(e) => e.stopPropagation()}
              />

              <button
                className="
                  absolute -top-3 -right-3
                  w-10 h-10 rounded-2xl
                  bg-white/80 dark:bg-black/60 backdrop-blur-md
                  border border-black/10 dark:border-white/10
                  text-gray-900 dark:text-white
                  hover:scale-[1.03] transition
                "
                onClick={(e) => {
                  e.stopPropagation();
                  closeModal();
                }}
                aria-label={language === "FR" ? "Fermer" : "Close"}
                title={language === "FR" ? "Fermer" : "Close"}
              >
                ×
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default LatestNews;
